import { computed, reactive } from 'vue'
import { logoutAccount, readAccountAccess, saveAccountProfile, startWeChatBinding, type AccountAccess } from '../api/accountAccess'
import { ApiError } from '../api/client'
import type { Account, WeChatProvider } from '../types/domain'

type Status = 'loading' | 'ready' | 'signed-out' | 'expired' | 'switched' | 'error'
export const accountMenu = reactive({
  visible: false,
  account: null as Account | null,
  expectedAccountId: null as string | null,
  provider: 'web' as WeChatProvider,
  access: null as AccountAccess | null,
  status: 'loading' as Status,
  step: 'profile' as 'profile' | 'bind' | 'edit-name',
  nameDraft: '',
  password: '',
  busy: null as 'access' | 'bind' | 'logout' | 'profile' | null,
  error: '',
  notice: '',
})
let generation = 0
let profileRevision = 0
const savedNames = new Map<string, { name: string; revision: number }>()
const pendingProfileSaves = reactive(new Set<string>())
type AccountMenuCallbacks = { changePassword: () => void; joinTeam: () => void; profileSaved?: (account: Account) => void }
let callbacks: AccountMenuCallbacks = { changePassword: () => {}, joinTeam: () => {} }

export function connectAccountMenu(handlers: AccountMenuCallbacks) { callbacks = handlers }
const trimmedName = (value: string) => value.replace(/^[\p{Z}\s]+|[\p{Z}\s]+$/gu, '')
export const nicknameCharacterCount = computed(() => Array.from(trimmedName(accountMenu.nameDraft)).length)
export const nicknameSavePending = computed(() => pendingProfileSaves.has(accountMenu.expectedAccountId ?? ''))
export const passwordLoginEnabled = computed(() => accountMenu.account?.passwordLoginEnabled !== false)
export const currentProviderBound = computed(() => accountMenu.account?.wechatBindings?.[accountMenu.provider] === true)
export const anyWeChatBound = computed(() => accountMenu.account?.wechatBound === true
  || accountMenu.account?.wechatBindings?.web === true || accountMenu.account?.wechatBindings?.oa === true)
export const canBindWeChat = computed(() => accountMenu.status === 'ready' && passwordLoginEnabled.value
  && accountMenu.access?.wechatLoginAvailable?.[accountMenu.provider] === true && !currentProviderBound.value)
export const accountRoleLabel = computed(() => accountMenu.account?.teamRole === 'personal' ? '个人账号'
  : accountMenu.account?.role === 'admin' ? '团队管理员'
    : accountMenu.account?.role === 'moderator' ? '内容审核员' : '团队成员')

async function readLatestMenuAccess(): Promise<AccountAccess> {
  const startedAt = profileRevision, access = await readAccountAccess()
  const saved = access.account && savedNames.get(access.account.id)
  // An access request begun before a completed save may contain the old name.
  if (saved && saved.revision > startedAt && access.account) access.account = { ...access.account, name: saved.name }
  return access
}

function acceptAccess(access: AccountAccess): boolean {
  accountMenu.access = access
  if (!access.canAccessWorkspace || !access.account) {
    accountMenu.status = accountMenu.expectedAccountId ? 'expired' : 'signed-out'
    return false
  }
  if (access.account.id !== accountMenu.expectedAccountId) {
    accountMenu.status = 'switched'
    return false
  }
  accountMenu.account = access.account
  accountMenu.status = 'ready'
  return true
}

export async function refreshAccountMenu() {
  if (!accountMenu.visible || accountMenu.busy) return
  const attempt = generation
  accountMenu.busy = 'access'; accountMenu.error = ''; accountMenu.status = 'loading'
  try {
    const access = await readLatestMenuAccess()
    if (attempt === generation) acceptAccess(access)
  } catch (error) {
    if (attempt === generation) {
      if (error instanceof ApiError && error.status === 401) accountMenu.status = accountMenu.expectedAccountId ? 'expired' : 'signed-out'
      else {
        accountMenu.status = 'error'
        accountMenu.error = error instanceof Error ? error.message : '暂时无法确认当前账号，请重试。'
      }
    }
  } finally { if (attempt === generation) accountMenu.busy = null }
}

export function openAccountMenu(cachedAccount: Account | null) {
  generation++
  Object.assign(accountMenu, { visible: true, account: cachedAccount, expectedAccountId: cachedAccount?.id ?? null,
    provider: /MicroMessenger/i.test(navigator.userAgent) ? 'oa' : 'web', access: null, status: 'loading',
    step: 'profile', nameDraft: '', password: '', busy: null, error: '', notice: '' })
  void refreshAccountMenu()
}

export function closeAccountMenu() {
  generation++
  accountMenu.visible = false; accountMenu.busy = null; accountMenu.nameDraft = ''; accountMenu.password = ''; accountMenu.error = ''
}

export function beginNicknameEditing() {
  if (accountMenu.status !== 'ready' || accountMenu.busy || nicknameSavePending.value || !accountMenu.account) return
  accountMenu.step = 'edit-name'; accountMenu.nameDraft = accountMenu.account.name; accountMenu.error = ''; accountMenu.notice = ''
}

export function cancelNicknameEditing() {
  generation++
  accountMenu.busy = null; accountMenu.step = 'profile'; accountMenu.nameDraft = ''; accountMenu.error = ''
}

export async function submitNickname() {
  if (!accountMenu.visible || accountMenu.status !== 'ready' || accountMenu.busy || nicknameSavePending.value || accountMenu.step !== 'edit-name') return
  const name = trimmedName(accountMenu.nameDraft)
  if (/[\u0000-\u001f\u007f\u0085\u2028\u2029]/u.test(accountMenu.nameDraft)) { accountMenu.error = '昵称不能包含控制字符或换行。'; return }
  if (!name || Array.from(name).length > 40) { accountMenu.error = '昵称请输入 1–40 个字符。'; return }
  const attempt = generation, accountId = accountMenu.expectedAccountId!
  accountMenu.busy = 'profile'; accountMenu.error = ''
  pendingProfileSaves.add(accountId)
  try {
    const access = await readLatestMenuAccess()
    if (attempt !== generation || !acceptAccess(access)) return
    const saved = await saveAccountProfile(accountId, name)
    // A completed save still refreshes the same account after closing/cancelling,
    // but never carries account A's response into a later account B session.
    if (accountMenu.expectedAccountId !== accountId || ['switched', 'expired', 'signed-out'].includes(accountMenu.status)) return
    savedNames.set(accountId, { name: saved.name, revision: ++profileRevision })
    if (accountMenu.account?.id === accountId) accountMenu.account = { ...accountMenu.account, name: saved.name }
    try { callbacks.profileSaved?.(saved) } catch { /* The nickname is already persisted; a view callback cannot turn success into failure. */ }
    if (attempt === generation && accountMenu.visible) {
      accountMenu.step = 'profile'; accountMenu.nameDraft = ''; accountMenu.notice = '昵称已更新'
    }
  } catch (error) {
    if (attempt !== generation) return
    if (error instanceof ApiError && error.code === 'ACCOUNT_CHANGED') accountMenu.status = 'switched'
    else if (error instanceof ApiError && error.status === 401) accountMenu.status = 'expired'
    else if (error instanceof ApiError && error.status === 403) {
      accountMenu.status = 'error'; accountMenu.error = error.message
    } else accountMenu.error = error instanceof Error ? error.message : '昵称保存失败，请重试。'
  } finally {
    pendingProfileSaves.delete(accountId)
    if (attempt === generation) accountMenu.busy = null
  }
}

export function beginWeChatBinding() {
  if (!canBindWeChat.value || accountMenu.busy) return
  accountMenu.step = 'bind'; accountMenu.error = ''; accountMenu.notice = ''
}

export function cancelWeChatBinding() {
  // A response from an abandoned request must never redirect a closed form.
  generation++
  accountMenu.busy = null; accountMenu.step = 'profile'; accountMenu.password = ''; accountMenu.error = ''
}

export async function submitWeChatBinding() {
  if (!accountMenu.visible || accountMenu.busy || !canBindWeChat.value || accountMenu.step !== 'bind') return
  if (!accountMenu.password) { accountMenu.error = '请输入当前账号密码。'; return }
  const attempt = generation, password = accountMenu.password, accountId = accountMenu.expectedAccountId!, provider = accountMenu.provider
  accountMenu.busy = 'bind'; accountMenu.error = ''
  try {
    const access = await readLatestMenuAccess()
    if (attempt !== generation || !acceptAccess(access)) return
    if (!canBindWeChat.value) {
      accountMenu.step = 'profile'
      accountMenu.notice = currentProviderBound.value ? '微信已绑定' : '当前账号无法使用密码绑定微信。'
      return
    }
    const url = await startWeChatBinding(password, accountId, provider)
    if (attempt === generation && accountMenu.visible) location.assign(url)
  } catch (error) {
    if (attempt !== generation) return
    if (error instanceof ApiError && (error.code === 'ACCOUNT_CHANGED' || error.code === 'ACCOUNT_CONTEXT_REQUIRED')) {
      accountMenu.status = 'switched'
    } else if (error instanceof ApiError && error.status === 401) accountMenu.status = 'expired'
    else if (error instanceof ApiError && ['WECHAT_ALREADY_BOUND', 'PASSWORD_LOGIN_UNAVAILABLE'].includes(error.code)) {
      try {
        const access = await readLatestMenuAccess()
        if (attempt === generation && acceptAccess(access)) {
          accountMenu.step = 'profile'
          accountMenu.notice = error.message
          if (canBindWeChat.value) { accountMenu.status = 'error'; accountMenu.error = '账号信息已更新，请重新确认账号。' }
        }
      } catch {
        if (attempt === generation) { accountMenu.status = 'error'; accountMenu.error = '账号信息已更新，请重新确认账号。' }
      }
    } else accountMenu.error = error instanceof Error ? error.message : '微信绑定暂不可用，请重试。'
  } finally { if (attempt === generation) accountMenu.busy = null }
}

export function openAccountPasswordChange() {
  if (accountMenu.status !== 'ready' || accountMenu.busy || !passwordLoginEnabled.value) return
  closeAccountMenu(); callbacks.changePassword()
}

export function openAccountTeamJoin() {
  if (accountMenu.status !== 'ready' || accountMenu.busy || accountMenu.account?.teamRole !== 'personal') return
  closeAccountMenu(); callbacks.joinTeam()
}

export async function logoutCurrentAccount() {
  if (accountMenu.status !== 'ready' || accountMenu.busy) return
  const attempt = generation
  accountMenu.busy = 'logout'; accountMenu.error = ''
  try {
    await logoutAccount()
    // Logout changes the server session even when this dialog was dismissed.
    location.replace('/library')
  } catch (error) {
    if (attempt === generation) accountMenu.error = error instanceof Error ? error.message : '退出登录失败，请重试。'
  } finally { if (attempt === generation) accountMenu.busy = null }
}
